import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";
import { AuthProvider } from "@/lib/auth";
import ProtectedRoute from "@/components/ProtectedRoute";
import OwnerOnlyRoute from "@/components/OwnerOnlyRoute";
import WorkspaceHome from "@/pages/WorkspaceHome";
import MyProjects from "@/pages/MyProjects";
import SecuritySettings from "@/pages/SecuritySettings";
import SignInSecurityPolicy from "@/pages/admin/SignInSecurityPolicy";
import AiUsageLimits from "@/pages/admin/AiUsageLimits";
import ModulesSettings from "@/pages/admin/ModulesSettings";
import MfaRecover from "@/pages/public/MfaRecover";
import MySubcontract from "@/pages/projects/MySubcontract";
import ProjectAccessSettings from "@/pages/admin/ProjectAccessSettings";
import { homeRouteFor } from "@/lib/roleHome";
import Landing from "@/pages/public/Home";
import Login from "@/pages/Login";
import RotatePassword from "@/pages/RotatePassword";
import ResetPassword from "@/pages/ResetPassword";
import ChangePassword from "@/pages/ChangePassword";
// R1-VISUAL Slice 4 — About the Owner (public).
import AboutOwner from "@/pages/public/AboutOwner";
// R1e — A2P 10DLC-compliant public policy pages at canonical routes.
import PrivacyPolicy from "@/pages/public/PrivacyPolicy";
import TermsAndConditions from "@/pages/public/TermsAndConditions";
import OwnerCommandCenter from "@/pages/OwnerCommandCenter";
import OwnerBrief from "@/pages/OwnerBrief";

import ModuleStatusPage from "@/pages/ModuleStatusPage";
import NotFound from "@/pages/NotFound";
import PortfolioGate from "@/pages/admin/portfolio/PortfolioGate";
import PortfolioList from "@/pages/admin/portfolio/PortfolioList";
import PortfolioNew from "@/pages/admin/portfolio/PortfolioNew";
import PortfolioDetail from "@/pages/admin/portfolio/PortfolioDetail";
import PortfolioReview from "@/pages/admin/portfolio/PortfolioReview";
import PortfolioInvite from "@/pages/admin/portfolio/PortfolioInvite";
import ArchitectGate from "@/pages/phase3/ArchitectGate";
import ArchitectWorkspace from "@/pages/phase3/ArchitectWorkspace";
import ArchitectDocument from "@/pages/phase3/ArchitectDocument";
import RfiDetail from "@/pages/phase3/RfiDetail";
import ArchitectPortal from "@/pages/phase3/ArchitectPortal";
import SubmissionDetail from "@/pages/phase3/SubmissionDetail";
import FieldWorkspace, { FieldGate } from "@/pages/phase4/FieldWorkspace";
import DailyLogDetail from "@/pages/phase4/DailyLogDetail";
import BillingWorkspace, { FinanceGate } from "@/pages/phase5/BillingWorkspace";
import PayAppDetail from "@/pages/phase5/PayAppDetail";
import ClientSharing, { SharingGate } from "@/pages/phase6/ClientSharing";
import ClientPortalEntry from "@/pages/phase6/ClientPortal";
import MagicLink from "@/pages/phase6/MagicLink";
import WalkthroughAccess from "@/pages/WalkthroughAccess";
import { PropertyGate, PropertyList, PropertyDetail } from "@/pages/phase7/Property";
import { IqGate, IqHome, BriefDetail } from "@/pages/phase8/Iq";
import FounderRules from "@/pages/admin/FounderRules";
import PublicPortfolioProject from "@/pages/public/PortfolioProject";
import Quote from "@/pages/Quote";
import Reports from "@/pages/Reports";
import TenantsAdmin from "@/pages/admin/Tenants";
import UsersAdmin from "@/pages/admin/Users";
import RolesAdmin from "@/pages/admin/Roles";
import AuthorityAdmin from "@/pages/admin/Authority";
import ApprovalsAdmin from "@/pages/admin/Approvals";
import AuditAdmin from "@/pages/admin/Audit";
import DocumentsAdmin from "@/pages/admin/Documents";
import CredentialsAdmin from "@/pages/admin/Credentials";
import NotificationsOutbox from "@/pages/admin/NotificationsOutbox";
import FeatureFlagsAdmin from "@/pages/admin/FeatureFlags";
import IntegrationsAdmin from "@/pages/admin/Integrations";
import PortalSwitch from "@/pages/admin/PortalSwitch";
import TradeCatalog from "@/pages/admin/TradeCatalog";
import FeatureRequests from "@/pages/admin/FeatureRequests";
import NotificationTemplates from "@/pages/admin/NotificationTemplates";
import PaymentsFallback from "@/pages/admin/PaymentsFallback";
import HelpCenter from "@/pages/HelpCenter";
import Leads from "@/pages/crm/Leads";
import LeadDetail from "@/pages/crm/LeadDetail";
import Opportunities from "@/pages/crm/Opportunities";
import OpportunityDetail from "@/pages/crm/OpportunityDetail";
import SiteVisitsCalendar from "@/pages/crm/SiteVisitsCalendar";
import EstimateEditor from "@/pages/crm/EstimateEditor";
import RateLibrary from "@/pages/crm/RateLibrary";
import ContractTemplates from "@/pages/crm/ContractTemplates";
import ProjectSchedule from "@/pages/pm/ProjectSchedule";
import MyTasks from "@/pages/pm/MyTasks";
import ProgressReport from "@/pages/pm/ProgressReport";
import ProjectSetup from "@/pages/pm/ProjectSetup";
import ProjectThreads from "@/pages/pm/ProjectThreads";
import FieldHome from "@/pages/field/FieldHome";
import FieldReportEditor from "@/pages/field/FieldReportEditor";
import FieldReportSearch from "@/pages/field/FieldReportSearch";
import ProjectSafety from "@/pages/hse/ProjectSafety";
import CompanyInsurance from "@/pages/hse/CompanyInsurance";
import FinanceHome, { ProjectJobCost } from "@/pages/finance/FinanceHome";
import WorkforceHome from "@/pages/workforce/WorkforceHome";
import NotificationCenter from "@/pages/comms/NotificationCenter";
import ProjectComms from "@/pages/comms/ProjectComms";
import DeliveryRecovery from "@/pages/comms/DeliveryRecovery";
import ShareDownload from "@/pages/public/ShareDownload";
import ProjectCloseout from "@/pages/pm/ProjectCloseout";
import ModuleGate from "@/components/ModuleGate";
import CrmRecordPage from "@/pages/crm/CrmRecordPage";
import ClientSign from "@/pages/public/ClientSign";
import ClientProposal from "@/pages/public/ClientProposal";
import Estimates from "@/pages/crm/Estimates";
import Proposals from "@/pages/crm/Proposals";
import Contracts from "@/pages/crm/Contracts";
import Deposits from "@/pages/crm/Deposits";
import Contacts from "@/pages/crm/Contacts";
import Pipeline from "@/pages/crm/Pipeline";
import Activities from "@/pages/crm/Activities";
import Learn from "@/pages/Learn";
import ProjectShell from "@/pages/projects/ProjectShell";
import ProjectPermits from "@/pages/projects/ProjectPermits";
import PublicProposal from "@/pages/public/PublicProposal";
import PublicSign from "@/pages/public/PublicSign";
import PublicPay from "@/pages/public/PublicPay";
import PublicVerify from "@/pages/public/PublicVerify";
// B-Final-Fix (P7) — insurer-friendly Compliance Proof verifier landing.
import VerifyCompliance from "@/pages/public/VerifyCompliance";
import PeopleRolesShell from "@/pages/admin/PeopleRolesShell";
import FinancialShell from "@/pages/admin/FinancialShell";
import OperationsShell from "@/pages/admin/OperationsShell";
import AuditTrustShell from "@/pages/admin/AuditTrustShell";
// R1b-β Step 4 — Platform Billing surface + role-scoped route gate.
import BillingAdmin from "@/pages/admin/Billing";
import AllowedRolesRoute from "@/components/AllowedRolesRoute";
import AdminOrOwnerRoute from "@/components/AdminOrOwnerRoute";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import ClientPortalPlaceholder from "@/pages/client/ClientPortalPlaceholder";
import BidProposals from "@/pages/crm/BidProposals";
import BidProposalNew from "@/pages/crm/BidProposalNew";
import BidProposalDetail from "@/pages/crm/BidProposalDetail";
// Release A · B0-Closeout — Owner Inbox + Communications Settings.
import OwnerInbox from "@/pages/admin/OwnerInbox";
import CommunicationsSettings from "@/pages/admin/CommunicationsSettings";
// Release C-2 — Communications Workspace + Owner Preferences.
import Communications from "@/pages/admin/Communications";
import OwnerPreferences from "@/pages/admin/OwnerPreferences";
// Release B — public request-access page + Owner Access-Review workspace.
import RequestAccess from "@/pages/public/RequestAccess";
import RequestAccessVerify from "@/pages/public/RequestAccessVerify";
import AccessRequestsInbox from "@/pages/admin/AccessRequestsInbox";
// Release B-Fidelity-2a — Applicant Questionnaire + Documents + Owner Detail.
import AccessQuestionnaire from "@/pages/public/AccessQuestionnaire";
import AccessDocuments from "@/pages/public/AccessDocuments";
import AccessRequestDetail from "@/pages/admin/AccessRequestDetail";
// Release B-Fidelity-2b — Scheduling.
import AccessScheduling from "@/pages/public/AccessScheduling";
import OwnerSchedule from "@/pages/admin/OwnerSchedule";
// Release B-Fidelity-3b — Central Document Center.
import DocumentCenter from "@/pages/admin/DocumentCenter";
// Wave 2 — Material Price Intelligence.
import MaterialPrices from "@/pages/procurement/MaterialPrices";
import MaterialPriceNew from "@/pages/procurement/MaterialPriceNew";
import MaterialPriceCompare from "@/pages/procurement/MaterialPriceCompare";
// Wave 3 — Public marketing site + admin website management.
import PublicHome from "@/pages/public/Home";
import PublicServices from "@/pages/public/Services";
import PublicServiceDetail from "@/pages/public/ServiceDetail";
import CategoryNotFound from "@/pages/public/CategoryNotFound";
import PublicPortfolio from "@/pages/public/Portfolio";
import PublicAbout from "@/pages/public/About";
import PublicServiceAreas from "@/pages/public/ServiceAreas";
import PublicServiceAreaDetail from "@/pages/public/ServiceAreaDetail";
import PublicQuote from "@/pages/public/QuotePublic";
import PublicContact from "@/pages/public/Contact";
import LegalTerms from "@/pages/public/legal/Terms";
import LegalPrivacy from "@/pages/public/legal/Privacy";
import WebsiteAdmin from "@/pages/admin/website/WebsiteAdmin";
// R1b-β Step 1 Deliverable C — Unified settings shell.
import SettingsShell from "@/pages/admin/SettingsShell";
// R1b-β Step 2 — User detail + public accept-invite.
import UserDetail from "@/pages/admin/UserDetail";
import ApprovalDetail from "@/pages/admin/ApprovalDetail";
import AcceptInvite from "@/pages/public/AcceptInvite";
import { useAuth } from "@/lib/auth";

function P({ children }) { return <ProtectedRoute>{children}</ProtectedRoute>; }
function O({ children }) { return <OwnerOnlyRoute>{children}</OwnerOnlyRoute>; }
function A({ children }) { return <AdminOrOwnerRoute>{children}</AdminOrOwnerRoute>; }
function OPO({ children }) {
  // R1b-β Step 4 — Owner + Platform Operator (used by /admin/billing and /admin/tenants).
  return <AllowedRolesRoute roles={["OWNER_SUPER_ADMIN", "PLATFORM_OPERATOR"]}>{children}</AllowedRolesRoute>;
}
// B2a: /phase/:module shows the module's real state for this workspace (no roadmap placeholder).
function PhaseParamRoute() {
  return <ModuleStatusPage />;
}
// Wave 3 — Auth-branching root: unauthenticated visitors see the public Home;
// authenticated users are routed to their role home so `/` never dead-ends
// on the marketing page after login.
function RootLanding() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) {
    return <Navigate to={homeRouteFor(user)} replace />;
  }
  return <PublicHome />;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        {/* R1c dead-click fix F1+F2 (Round 2) — Toaster relocated to
            bottom-right so success/error toasts never cover the fixed
            top impersonation banner's `portal-stop-impersonation` button.
            Real UX bug: at top-right the "Impersonating as X" toast
            physically blocked the Return-to-Owner exit affordance. */}
        <Toaster position="bottom-right" richColors closeButton />
        {/* R1c dead-click fix F1+F2 (Round 2) — Global impersonation banner.
            Mounted once above <Routes> so any startImpersonation() call
            (top-bar PortalSwitcher, OwnerCommandCenter dropdown, or
            /admin/portal-switch picker) causes the banner to mount within
            one React tick — no page-local duplicates. */}
        <ImpersonationBanner />
        <Routes>
          <Route path="/" element={<RootLanding />} />
          <Route path="/login" element={<Login />} />
          <Route path="/auth/magic" element={<MagicLink />} />
          <Route path="/auth/walkthrough" element={<WalkthroughAccess />} />
          <Route path="/client-login" element={<Navigate to="/login" replace />} />
          <Route path="/quote" element={<PublicQuote />} />
          <Route path="/rotate-password" element={<RotatePassword />} />
          <Route path="/auth/rotate" element={<RotatePassword />} />
          {/* Password reset landing — consumes token issued by /auth/forgot-password. */}
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/mfa/recover" element={<MfaRecover />} />
          <Route path="/auth/reset-password" element={<ResetPassword />} />
          {/* R1d.14 — self-service password change for already-authenticated Owners.
              Distinct from /auth/rotate (forced first-login rotation). */}
          <Route path="/account/password" element={<P><ChangePassword /></P>} />
          <Route path="/auth/change-password" element={<P><ChangePassword /></P>} />

          {/* Wave 3 — Public marketing website (unauthenticated). */}
          <Route path="/services" element={<PublicServices />} />
          {/* R1d.8 — canonical category deep-link routes; unknown slug renders CategoryNotFound. */}
          <Route path="/services/category/:categorySlug" element={<PublicServices />} />
          <Route path="/services/category-not-found/:slug" element={<CategoryNotFound />} />
          <Route path="/services/:slug" element={<PublicServiceDetail />} />
          <Route path="/portfolio" element={<PublicPortfolio />} />
          <Route path="/portfolio/:id" element={<PublicPortfolioProject />} />
          <Route path="/about" element={<PublicAbout />} />
          <Route path="/service-areas" element={<PublicServiceAreas />} />
          <Route path="/service-areas/:slug" element={<PublicServiceAreaDetail />} />
          <Route path="/contact" element={<PublicContact />} />
          {/* R1e — A2P 10DLC canonical routes. Legacy /legal/* aliases below
              redirect to preserve any inbound links; old page components
              remain in the tree (dormant) until inbound-link audit clears. */}
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
          {/* R1e-Freeze-A2P — /sms-terms canonical alias for the Twilio Trust
              Hub packet. Deep-links directly to the SMS/Messaging Terms section
              inside the Terms & Conditions page so submitters land exactly at
              the disclosure block referenced by the opt-in text. */}
          <Route path="/sms-terms" element={<Navigate to="/terms-and-conditions#sms-messaging-terms" replace />} />
          <Route path="/about-owner" element={<AboutOwner />} />
          <Route path="/about" element={<AboutOwner />} />
          <Route path="/legal/terms" element={<Navigate to="/terms-and-conditions" replace />} />
          <Route path="/legal/privacy" element={<Navigate to="/privacy-policy" replace />} />

          {/* Wave 3 — Admin website content management. */}
          <Route path="/admin/website" element={<A><WebsiteAdmin /></A>} />
          <Route path="/admin/portfolio" element={<P><PortfolioGate><PortfolioList /></PortfolioGate></P>} />
          <Route path="/admin/portfolio/invite" element={<P><PortfolioGate><PortfolioInvite /></PortfolioGate></P>} />
          <Route path="/admin/portfolio/new" element={<P><PortfolioGate><PortfolioNew /></PortfolioGate></P>} />
          <Route path="/admin/portfolio/:id" element={<P><PortfolioGate><PortfolioDetail /></PortfolioGate></P>} />
          <Route path="/admin/portfolio/:id/review" element={<P><PortfolioGate><PortfolioReview /></PortfolioGate></P>} />
          <Route path="/phase3/architect" element={<P><ArchitectGate><ArchitectWorkspace /></ArchitectGate></P>} />
          <Route path="/phase3/architect/documents/:id" element={<P><ArchitectGate><ArchitectDocument /></ArchitectGate></P>} />
          <Route path="/phase3/rfis/:id" element={<P><ArchitectGate><RfiDetail /></ArchitectGate></P>} />
          <Route path="/phase3/architect/portal" element={<P><ArchitectGate><ArchitectPortal /></ArchitectGate></P>} />
          <Route path="/phase3/architect/submissions/:id" element={<P><ArchitectGate><SubmissionDetail /></ArchitectGate></P>} />
          <Route path="/phase4/field" element={<P><FieldGate><FieldWorkspace /></FieldGate></P>} />
          <Route path="/phase4/daily-logs/:id" element={<P><FieldGate><DailyLogDetail /></FieldGate></P>} />
          <Route path="/phase5/billing" element={<P><FinanceGate><BillingWorkspace /></FinanceGate></P>} />
          <Route path="/phase5/pay-apps/:id" element={<P><FinanceGate><PayAppDetail /></FinanceGate></P>} />
          <Route path="/phase6/sharing" element={<P><SharingGate><ClientSharing /></SharingGate></P>} />
          <Route path="/phase7/properties" element={<P><PropertyGate><PropertyList /></PropertyGate></P>} />
          <Route path="/phase7/properties/:id" element={<P><PropertyGate><PropertyDetail /></PropertyGate></P>} />
          <Route path="/phase8/iq" element={<P><IqGate><IqHome /></IqGate></P>} />
          <Route path="/admin/settings/founder-rules" element={<P><FounderRules /></P>} />
          <Route path="/phase8/briefs/:id" element={<P><IqGate><BriefDetail /></IqGate></P>} />
          <Route path="/admin/settings" element={<A><SettingsShell /></A>} />
          {/* B0-Closeout — Owner Inbox + Communications Settings surfaces. */}
          <Route path="/admin/owner/inbox" element={<A><OwnerInbox /></A>} />
          <Route path="/admin/settings/communications" element={<A><CommunicationsSettings /></A>} />
          {/* Release C-2 (P3) — Communications Workspace (Owner + Administrator only). */}
          <Route path="/admin/communications" element={<A><Communications /></A>} />
          {/* Release C-2 (P5) — Owner Notification Preferences (Owner-only). */}
          <Route path="/admin/settings/owner-preferences" element={<O><OwnerPreferences /></O>} />
          {/* Release B — public request-access + Owner Access-Review workspace. */}
          <Route path="/request-access" element={<RequestAccess />} />
          <Route path="/request-access/verify" element={<RequestAccessVerify />} />
          <Route path="/admin/access-requests" element={<A><AccessRequestsInbox /></A>} />
          <Route path="/admin/access-requests/:id" element={<A><AccessRequestDetail /></A>} />
          {/* Release B-Fidelity-2a — Applicant-scoped public token pages. */}
          <Route path="/access-request/:token/questionnaire" element={<AccessQuestionnaire />} />
          <Route path="/access-request/:token/documents" element={<AccessDocuments />} />
          {/* Release B-Fidelity-2b — Applicant scheduling + Owner scheduling admin. */}
          <Route path="/access-request/:token/scheduling" element={<AccessScheduling />} />
          <Route path="/admin/owner/schedule" element={<O><OwnerSchedule /></O>} />
          {/* 3a — Universal Intake dev-only demo (Owner-authed). */}
          {/* 3b — Central Document Center (Owner + Admin). */}
          <Route path="/admin/documents" element={<A><DocumentCenter /></A>} />
          <Route path="/admin/users/:id" element={<A><UserDetail /></A>} />
          <Route path="/admin/approvals/:id" element={<A><ApprovalDetail /></A>} />
          <Route path="/invite/accept" element={<AcceptInvite />} />

          {/* Public token pages (no auth) */}
          <Route path="/p/:token" element={<PublicProposal />} />
          <Route path="/sign/:token" element={<PublicSign />} />
          <Route path="/pay/:token" element={<PublicPay />} />
          <Route path="/client/sign/:token" element={<ClientSign />} />
          <Route path="/client/proposal/:token" element={<ClientProposal />} />
          <Route path="/verify/:proof" element={<PublicVerify />} />
          {/* B-Final-Fix (P7) — Compliance Proof verifier landing page.
              Public. No auth. Consumes /api/compliance-proof/verify/{token}
              in redacted mode. */}
          <Route path="/verify-compliance/:token" element={<VerifyCompliance />} />

          <Route path="/owner" element={<O><OwnerCommandCenter /></O>} />
          <Route path="/owner/brief" element={<A><OwnerBrief /></A>} />
          {/* B2a — workspace home: open to every signed-in role (landing page for roles without a module home). */}
          <Route path="/home" element={<P><WorkspaceHome /></P>} />
          <Route path="/my/projects" element={<P><MyProjects /></P>} />
          <Route path="/settings/security" element={<P><SecuritySettings /></P>} />
          <Route path="/projects/:id/my-subcontract" element={<P><MySubcontract /></P>} />
          <Route path="/admin/settings/project-access" element={<O><ProjectAccessSettings /></O>} />
          <Route path="/admin/settings/sign-in-security" element={<O><SignInSecurityPolicy /></O>} />
          <Route path="/admin/settings/ai-usage" element={<O><AiUsageLimits /></O>} />
          <Route path="/admin/settings/modules" element={<O><ModulesSettings /></O>} />

          {/* R1c dead-click fix F1+F2 (Round 2) — Client Portal placeholder.
              Owner impersonation of CLIENT role lands here (no more 404).
              Uses ProtectedRoute (not OwnerOnly) so the impersonated CLIENT
              context remains authoritative; the AdminAppShell renders a
              truthful "Coming in R4" body. */}
          <Route path="/client/portal" element={<P><ClientPortalEntry /></P>} />

          {/* R1b-β — People & Access tabbed surface + legacy aliases */}
          <Route path="/admin/people-roles" element={<Navigate to="/admin/people-roles/users" replace />} />
          <Route path="/admin/people-roles/:tab" element={<A><PeopleRolesShell /></A>} />
          <Route path="/admin/users" element={<Navigate to="/admin/people-roles/users" replace />} />
          <Route path="/admin/roles" element={<Navigate to="/admin/people-roles/roles" replace />} />
          <Route path="/admin/authority" element={<Navigate to="/admin/people-roles/authority" replace />} />

          {/* R1b-α iteration-5 — real routes for stub nav groups.
              Each renders under AdminAppShell with breadcrumbs + back button.
              Owner-only until R2 expands role access. */}
          <Route path="/admin/financial" element={<Navigate to="/admin/financial/overview" replace />} />
          <Route path="/admin/financial/:tab" element={<O><FinancialShell /></O>} />
          <Route path="/admin/billing" element={<OPO><BillingAdmin /></OPO>} />
          <Route path="/admin/contracts" element={<Navigate to="/admin/financial/contracts" replace />} />

          <Route path="/admin/operations" element={<Navigate to="/admin/operations/projects" replace />} />
          <Route path="/admin/operations/:tab" element={<A><OperationsShell /></A>} />
          <Route path="/admin/projects" element={<Navigate to="/admin/operations/projects" replace />} />

          {/* R1b-δ — Audit & Trust tabbed surface. Owner + Admin (compliance-reports gates owner-only in the shell). */}
          <Route path="/admin/audit-trust" element={<Navigate to="/admin/audit-trust/ledger" replace />} />
          <Route path="/admin/audit-trust/:tab" element={<A><AuditTrustShell /></A>} />
          <Route path="/reports" element={<P><Reports /></P>} />
          <Route path="/admin/tenants" element={<OPO><TenantsAdmin /></OPO>} />
          <Route path="/admin/approvals" element={<O><ApprovalsAdmin /></O>} />
          <Route path="/admin/audit" element={<O><AuditAdmin /></O>} />
          {/* R1b-δ.2 — legacy soak-fallbacks retired. Any bookmarks land on canonical. */}
          <Route path="/admin/users-legacy" element={<Navigate to="/admin/people-roles/users" replace />} />
          <Route path="/admin/roles-legacy" element={<Navigate to="/admin/people-roles/roles" replace />} />
          <Route path="/admin/authority-legacy" element={<Navigate to="/admin/people-roles/authority" replace />} />
          <Route path="/admin/documents-legacy" element={<Navigate to="/admin/operations/documents" replace />} />
          <Route path="/admin/credentials-legacy" element={<Navigate to="/admin/operations/compliance" replace />} />
          <Route path="/admin/documents" element={<Navigate to="/admin/operations/documents" replace />} />
          <Route path="/admin/credentials" element={<O><CredentialsAdmin /></O>} />
          <Route path="/admin/notifications/outbox" element={<O><NotificationsOutbox /></O>} />
          <Route path="/admin/notification-templates" element={<O><NotificationTemplates /></O>} />
          <Route path="/admin/payments-fallback" element={<O><PaymentsFallback /></O>} />
          <Route path="/admin/feature-flags" element={<O><FeatureFlagsAdmin /></O>} />
          <Route path="/admin/integrations" element={<A><IntegrationsAdmin /></A>} />
          <Route path="/admin/portal-switch" element={<O><PortalSwitch /></O>} />
          <Route path="/admin/trade-catalog" element={<O><TradeCatalog /></O>} />
          <Route path="/admin/feature-requests" element={<O><FeatureRequests /></O>} />
          <Route path="/help" element={<P><HelpCenter /></P>} />

          {/* R1c — CRM rehomed under AdminAppShell. Canonical paths + preserved deep links. */}
          <Route path="/crm" element={<Navigate to="/crm/leads" replace />} />
          <Route path="/crm/leads" element={<P><Leads /></P>} />
          <Route path="/crm/leads/:id" element={<P><LeadDetail /></P>} />
          <Route path="/crm/opportunities" element={<P><Opportunities /></P>} />
          <Route path="/crm/opportunities/:id" element={<P><OpportunityDetail /></P>} />
          <Route path="/crm/site-visits" element={<P><SiteVisitsCalendar /></P>} />
          <Route path="/crm/estimates" element={<P><Estimates /></P>} />
          <Route path="/crm/estimates/:id/edit" element={<P><EstimateEditor /></P>} />
          <Route path="/crm/estimating/rates" element={<P><RateLibrary /></P>} />
          <Route path="/crm/contract-templates" element={<P><ContractTemplates /></P>} />
          <Route path="/pm/projects/:pid/schedule" element={<P><ProjectSchedule /></P>} />
          <Route path="/pm/projects/:pid/progress-report" element={<P><ProgressReport /></P>} />
          <Route path="/pm/my-tasks" element={<P><MyTasks /></P>} />
          <Route path="/pm/projects/:pid/setup" element={<P><ProjectSetup /></P>} />
          <Route path="/pm/projects/:pid/threads" element={<P><ProjectThreads /></P>} />
          <Route path="/pm/projects/:pid/closeout" element={<P><ModuleGate moduleKey="closeout"><ProjectCloseout /></ModuleGate></P>} />
          <Route path="/field" element={<P><FieldHome /></P>} />
          <Route path="/field/projects/:pid/report" element={<P><FieldReportEditor /></P>} />
          <Route path="/field/reports" element={<P><FieldReportSearch /></P>} />
          <Route path="/field/reports/:id" element={<P><FieldReportEditor /></P>} />
          <Route path="/hse/projects/:pid" element={<P><ProjectSafety /></P>} />
          <Route path="/hse/insurance" element={<P><CompanyInsurance /></P>} />
          <Route path="/finance" element={<P><FinanceHome /></P>} />
          <Route path="/finance/projects/:pid/job-cost" element={<P><ProjectJobCost /></P>} />
          <Route path="/workforce" element={<P><WorkforceHome /></P>} />
          <Route path="/notifications" element={<P><ModuleGate moduleKey="communications"><NotificationCenter /></ModuleGate></P>} />
          <Route path="/comms/projects/:pid" element={<P><ModuleGate moduleKey="communications"><ProjectComms /></ModuleGate></P>} />
          {/* B7–B13 breadcrumb parents (AppShell links every path segment): send them to the canonical lists. */}
          {["/pm", "/pm/projects", "/hse", "/hse/projects", "/comms", "/comms/projects"].map((p) => (
            <Route key={p} path={p} element={<Navigate to="/admin/operations/projects" replace />} />
          ))}
          <Route path="/field/projects" element={<Navigate to="/field" replace />} />
          <Route path="/finance/projects" element={<Navigate to="/finance" replace />} />
          <Route path="/admin/delivery-recovery" element={<A><ModuleGate moduleKey="communications"><DeliveryRecovery /></ModuleGate></A>} />
          <Route path="/share/:token" element={<ShareDownload />} />
          <Route path="/crm/contacts/:id" element={<P><CrmRecordPage kind="contact" /></P>} />
          <Route path="/crm/accounts/:id" element={<P><CrmRecordPage kind="account" /></P>} />
          <Route path="/crm/proposals" element={<P><Proposals /></P>} />
          {/* R1c.3a-v3 — Bid proposals (Owner-driven upload flow). Distinct from
              /crm/proposals (Phase 2 auto-generated proposal-to-client). */}
          <Route path="/crm/bid-proposals" element={<P><BidProposals /></P>} />
          <Route path="/crm/bid-proposals/new" element={<P><BidProposalNew /></P>} />
          <Route path="/crm/bid-proposals/:id" element={<P><BidProposalDetail /></P>} />
          <Route path="/crm/contracts" element={<P><Contracts /></P>} />
          <Route path="/crm/deposits" element={<P><Deposits /></P>} />
          <Route path="/crm/contacts" element={<P><Contacts /></P>} />
          <Route path="/crm/pipeline" element={<P><Pipeline /></P>} />
          <Route path="/crm/activities" element={<P><Activities /></P>} />

          {/* R1c — Project Command Center under AdminAppShell. Canonical list stays at /admin/operations/projects. */}
          <Route path="/projects" element={<Navigate to="/admin/operations/projects" replace />} />
          <Route path="/projects/:id" element={<P><ProjectShell /></P>} />
          {/* Wave 4b — Project-scoped permit workflow. */}
          <Route path="/projects/:id/permits" element={<P><ProjectPermits /></P>} />

          {/* Wave 2 — Material Price Intelligence (procurement). */}
          <Route path="/procurement" element={<Navigate to="/procurement/prices" replace />} />
          <Route path="/procurement/prices" element={<P><MaterialPrices /></P>} />
          <Route path="/procurement/prices/new" element={<P><MaterialPriceNew /></P>} />
          <Route path="/procurement/prices/compare" element={<P><MaterialPriceCompare /></P>} />
          <Route path="/procurement/prices/:id" element={<P><MaterialPriceNew /></P>} />

          {/* R1c — Learn hub. */}
          <Route path="/learn" element={<P><Learn /></P>} />

          <Route path="/phase/:module" element={<P><PhaseParamRoute /></P>} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
